// Raw sources: recoloured to the selected theme at runtime (themedSvg).
import ImpactIllustration from "./ImpactGrowth.svg?raw";
import FeaturesIllustration from "./FeaturesCanvas.svg?raw";
import TestimonialsIllustration from "./TestimonialsCircle.svg?raw";
import FaqIllustration from "./FaqBlueprint.svg?raw";
import LogoIllustration from "./logo_total.png";
import TourMoney from "./TourMoney.svg";
import TourPeople from "./TourPeople.svg";
import TourSalesScreenshot from "./TourSales.webp";
import TourSalesScreenshot960 from "./TourSales-960.webp";
import TourCustomers from "./TourCustomers.svg";
import type { Theme } from "@mui/material/styles";
import { siteTokens } from "../siteTokens";
import { themedSvgUrl } from "../themedSvg";

// Product-tour screenshots — keyed by tab id used in site.json
// (features.groups[i].tourKey). `sales` is a real product screenshot
// (sourced from the marketing prototype's own asset handoff); the other
// three are still the placeholder SVGs — swap each for a real screenshot
// once one exists for that area.
export const TOUR_SCREENSHOTS: Record<string, string> = {
  money: TourMoney,
  people: TourPeople,
  sales: TourSalesScreenshot,
  customers: TourCustomers
};

/** srcSet for the `sales` screenshot (960 and 1600 px wide) — pair it with a
 *  `sizes` matching where it's shown, so a 946 px slot doesn't download the
 *  1600 px file. */
export const TOUR_SALES_SRCSET = `${TourSalesScreenshot960} 960w, ${TourSalesScreenshot} 1600w`;

export type SectionIllustrationKey =
  | "impact"
  | "features"
  | "testimonials"
  | "faq"
  | "finalCta";

export interface SectionIllustration {
  image?: string;
  alt?: string;
  accent: string;
  overlayOpacity?: number;
}

export type SectionIllustrationOverrides = Partial<Record<SectionIllustrationKey, Partial<SectionIllustration>>>;

/** Accent washes per section, derived from the selected theme's tokens. */
const sectionAccents = (tk: ReturnType<typeof siteTokens>): Record<SectionIllustrationKey, string> => ({
  impact: `linear-gradient(135deg, ${tk.accentA(0.12)} 0%, ${tk.accent2A(0.08)} 38%, ${tk.accentA(0.14)} 100%)`,
  features: `linear-gradient(180deg, ${tk.accentA(0.08)} 0%, ${tk.accent2A(0.05)} 65%, ${tk.surface} 100%)`,
  testimonials: `linear-gradient(180deg, ${tk.accentA(0.1)} 0%, ${tk.accent2A(0.08)} 70%, ${tk.surface} 100%)`,
  faq: `linear-gradient(180deg, ${tk.accent2A(0.1)} 0%, ${tk.accentA(0.08)} 70%, ${tk.surface} 100%)`,
  finalCta: `linear-gradient(135deg, ${tk.accentA(0.95)} 0%, ${tk.accent2A(0.92)} 100%)`
});

// SVG sources per section; turned into themed data URIs on demand.
const SECTION_IMAGES: Partial<Record<SectionIllustrationKey, string>> = {
  impact: ImpactIllustration,
  features: FeaturesIllustration,
  testimonials: TestimonialsIllustration,
  faq: FaqIllustration
};

/**
 * Section illustration + accent wash for the selected theme: the accent wash
 * comes from the theme tokens and the SVG is recoloured to the theme.
 */
export const getSectionIllustration = (
  key: SectionIllustrationKey,
  overrides: SectionIllustrationOverrides | undefined,
  theme: Theme
): SectionIllustration => {
  const source = SECTION_IMAGES[key];
  return {
    image: key === "finalCta" ? LogoIllustration : source ? themedSvgUrl(source, theme) : undefined,
    accent: sectionAccents(siteTokens(theme))[key],
    ...(key === "finalCta" ? { overlayOpacity: 0.2 } : {}),
    ...(overrides?.[key] ?? {})
  };
};
